import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BRAND } from "../mock";
import { EnquiryForm } from "../components/EnquiryForm";
import {
  ArrowUpRight, Lock, ShieldCheck, Link2, BookMarked, Users, GitBranch,
  Sparkles, Building2, LineChart, Check, ArrowRight, Languages, MessageCircle, Zap, Play, Youtube, Mic,
} from "lucide-react";

const MB_SITE = "https://moroccanbrothers.com";

// Reveal-on-scroll without a motion library.
function useReveal() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } },
      { rootMargin: "-60px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, seen];
}

const Reveal = ({ children, delay = 0, className = "" }) => {
  const [ref, seen] = useReveal();
  return (
    <div ref={ref} style={{ animationDelay: `${delay}ms` }}
      className={`${seen ? "animate-fade-up" : "opacity-0"} ${className}`}>
      {children}
    </div>
  );
};

// App screenshots framed like a product shot.
const Shot = ({ src, alt, session, tone = "red", className = "" }) => (
  <div className={`relative ${className}`}>
    <div aria-hidden className={`absolute -inset-6 -z-10 rounded-[2rem] blur-3xl ${tone === "green" ? "bg-brand-green/10" : "bg-brand-red/10"}`} />
    <figure className="overflow-hidden rounded-2xl border border-white/10 bg-[#0b0f13] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]">
      <div className="flex items-center gap-3 border-b border-white/[0.06] bg-white/[0.02] px-4 py-2.5">
        <span className="flex items-center gap-1.5">
          <i className="h-2.5 w-2.5 rounded-full bg-brand-red/60" />
          <i className="h-2.5 w-2.5 rounded-full bg-amber-500/60" />
          <i className="h-2.5 w-2.5 rounded-full bg-brand-green/60" />
        </span>
        <span className="truncate font-display text-[11px] tracking-wide text-muted-foreground">{session}</span>
      </div>
      <img src={src} alt={alt} loading="lazy" className="block w-full" />
    </figure>
  </div>
);

const Eyebrow = ({ children, tone = "green" }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${
    tone === "amber" ? "border-amber-500/30 bg-amber-500/10 text-amber-400" : "border-brand-green/30 bg-brand-green/10 text-brand-green"}`}>
    {children}
  </span>
);

const FEATURES = [
  {
    key: "pipeline", icon: GitBranch, tone: "red",
    eyebrow: "Property CRM",
    title: "Every deal on one board, from first listing to keys in hand",
    body: "Drag each property through Sourced → Contacted → Viewing → Negotiating → Closing → Acquired. Each column totals what it's worth, every card carries its city and district, and nothing slips through because it was sitting in a WhatsApp thread.",
    points: ["Deal value per stage, live", "City tags and filters on every card", "Notes and tasks attached to the property"],
    shot: "/marketing/pipeline.jpeg", session: "Deal Pipeline · Marrakech",
  },
  {
    key: "importer", icon: Link2, tone: "green",
    eyebrow: "Mubawab importer",
    title: "Paste a Moroccan listing link. The deal builds itself",
    body: "Drop in a Mubawab URL and the photos, asking price in dirhams, district, size and the listing agent's phone number land straight in your pipeline — with the sterling and euro equivalents worked out for you. Re-import later and it refreshes the details without losing your progress.",
    points: ["Photos, MAD price, district, size", "Agent phone number pulled in", "MAD-first with £ and € conversion"],
    shot: "/marketing/properties.jpeg", session: "Properties · 6 riads & units",
  },
  {
    key: "agents", icon: Users, tone: "red",
    eyebrow: "Agent network",
    title: "Know which agents actually deliver",
    body: "Your own directory of Moroccan agents: how many of your properties came from each one, what that pipeline is worth, a reliability score you control, and whether they'll accept a sous-location lease. Call or WhatsApp them from the card.",
    points: ["Linked properties and total deal value", "Sous-location and direct-booking tracking", "One-tap call, WhatsApp and notes"],
    shot: "/marketing/agents.jpeg", session: "Agent Network · 4 agents",
  },
  {
    key: "blackbook", icon: BookMarked, tone: "green",
    eyebrow: "The Black Book",
    title: "The notaries, lawyers, banks and offices we already trust",
    body: "Buying in Morocco falls apart on paperwork. The Black Book is our vetted institutional directory — the notary board, land registry, bar associations, tax offices, the investment centre — each with a plain-English note on what it's for and when you need it. Alongside it you keep your own private contact book.",
    points: ["Vetted institutional directory, kept current", "Your own private contacts, never shared", "Search by category, city and tier"],
    shot: "/marketing/blackbook.jpeg", session: "Black Book · Institutional",
  },
];

const EXTRAS = [
  { icon: Building2, title: "Your own private workspace", body: "Every member gets an isolated CRM. Your deals, agents and contacts are yours — nobody else on the platform can see them." },
  { icon: Users, title: "Your success manager sees where you are", body: "Company setup, banking, sourcing, offer, live on Airbnb, optimising, scaling — your manager tracks the stage you're in so you never get left stuck." },
  { icon: LineChart, title: "Books that speak dirhams", body: "Track revenue and running costs per property, separate the operating costs from the luxuries, and see occupancy and ADR month by month." },
  { icon: ShieldCheck, title: "Approved access only", body: "Every sign-up is approved by hand. No scrapers, no tyre-kickers, no strangers inside the deal flow." },
];

const AI_POINTS = [
  { icon: Sparkles, t: "Claude reads the deal, not the brochure", d: "One tap and you get a rent-versus-price breakdown, realistic ADR and occupancy, a yield and payback estimate, a deal rating out of ten and a word-for-word negotiation script to send your agent." },
  { icon: Languages, t: "French and Arabic listings, in English, instantly", d: "Moroccan listings arrive in French or Arabic. Translate any description in place — English, Français, العربية, Nederlands, Español — without leaving the deal." },
  { icon: MessageCircle, t: "Straight into WhatsApp with the agent", d: "The importer pulls the agent's number off the listing, so their card has a live WhatsApp and call button. No copying numbers, no lost threads — and every agent sits on its own pipeline from first contact to active partner." },
  { icon: Zap, t: "The boring half is automated", d: "Paste a Mubawab link and the property builds itself — photos, dirham price, district, size, agent. Re-import it later and the details refresh while your stage, notes and negotiation stay exactly where they were." },
];

const PLANS = [
  {
    key: "software", name: "Software only", price: "€499", per: "per month",
    tagline: "The full platform, on its own.",
    points: [
      "Property deal pipeline and agent CRM",
      "Mubawab importer with agent phone capture",
      "Claude deal analysis, translation and Airbnb positioning",
      "The institutional Black Book",
      "Your own private, isolated workspace",
    ],
    cta: "Enquire about the software", highlight: false,
  },
  {
    key: "consultancy", name: "Moroccan Brothers consultancy", price: "Lifetime access", per: "included with membership",
    tagline: "The software, free for life — plus the people who make it work.",
    points: [
      "Everything in the software plan, for life",
      "One-to-one mentorship through your first acquisition",
      "A success manager tracking your journey stage by stage",
      "Our notaries, lawyers, bankers and contractors on speed dial",
      "Deal reviews before you commit a dirham",
    ],
    cta: "Learn more at moroccanbrothers.com", highlight: true,
  },
];

const CASE_VIDEOS = [
  { id: "WCqwEMbpMNc", title: "He Makes £7,000/Month From One Airbnb in Morocco", note: "One unit, run remotely" },
  { id: "LbUmGHQiD-E", title: "He Bought a £223K Airbnb in Morocco (Here's How Much It Makes Him)", note: "The numbers, in full" },
  { id: "xLRAeYcJYgs", title: "He Built a £5,000/Month Airbnb Business in Morocco From London", note: "Built from the UK" },
];

const PODCASTS = [
  { id: "pc5VWaIu8_4", title: "Property Investment in Morocco — Start Small, Big Returns", show: "Triple-A Talks · EP72" },
  { id: "x5ZNOXulKpw", title: "The Property Blueprint — How to Buy Property With £0", show: "Vibes & Value" },
  { id: "9rn679GA_6k", title: "From Council Estate to Property Mogul — Jamal's Journey", show: "Horizon Podcast" },
  { id: "99yneqH5ilk", title: "How Two Moroccan Brothers Built a Million-Pound Property Empire", show: "MarocafellaTV" },
];

// Click-to-play so seven iframes don't load with the page.
const VideoCard = ({ id, title, meta, testid, size = "lg" }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="group">
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0b0f13] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.9)]">
        <div className="relative aspect-video w-full">
          {playing ? (
            <iframe
              src={`https://www.youtube.com/embed/${id}?rel=0&playsinline=1`}
              title={title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
              allowFullScreen loading="lazy" className="absolute inset-0 h-full w-full border-0"
            />
          ) : (
            <button type="button" onClick={() => setPlaying(true)} aria-label={`Play: ${title}`}
              data-testid={testid} className="absolute inset-0 h-full w-full cursor-pointer">
              <img
                src={`https://i.ytimg.com/vi/${id}/maxresdefault.jpg`} alt={title} loading="lazy"
                onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`; }}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
              <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
              <span aria-hidden className={`absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-brand-red text-white shadow-2xl shadow-brand-red/40 transition-transform duration-300 group-hover:scale-110 ${size === "lg" ? "h-16 w-16" : "h-12 w-12"}`}>
                <Play className={size === "lg" ? "ml-0.5 h-6 w-6 fill-current" : "ml-0.5 h-5 w-5 fill-current"} />
              </span>
            </button>
          )}
        </div>
      </div>
      <p className={`mt-4 font-display font-semibold leading-snug text-white ${size === "lg" ? "text-base" : "text-sm"}`}>{title}</p>
      <p className="mt-1 text-xs uppercase tracking-[0.14em] text-muted-foreground">{meta}</p>
    </div>
  );
};

const STEPS = [
  { n: "01", t: "Apply to the consultancy", d: "Everything starts at moroccanbrothers.com — tell us where you are and what you're trying to build." },
  { n: "02", t: "Get enrolled", d: "Speak to the team, join a cohort, and get paired with the success manager who'll walk you through it." },
  { n: "03", t: "Your workspace is switched on", d: "Sign in, and your private CRM, the importer and the Black Book are waiting for you." },
];

export default function Landing() {
  const memberToken = typeof window !== "undefined" && localStorage.getItem("mb_crm_token");

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      {/* ambient depth */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-brand-red/[0.10] blur-[120px]" />
        <div className="absolute right-[-10rem] top-[30rem] h-[460px] w-[460px] rounded-full bg-brand-green/[0.08] blur-[120px]" />
        <div className="tech-grid absolute inset-0 opacity-[0.55]" />
      </div>

      {/* ---------------- nav ---------------- */}
      <header className="sticky top-3 z-50 mx-auto max-w-7xl px-4 sm:px-6">
        <nav className="flex items-center gap-4 rounded-2xl border border-white/[0.08] bg-[#0a0e12]/85 px-4 py-2.5 backdrop-blur-2xl">
          <img src={BRAND.logo} alt="Moroccan Brothers" width="500" height="250"
            className="h-12 w-auto shrink-0 object-contain sm:h-16 lg:h-20" />
          <div className="ml-4 hidden items-center gap-6 lg:flex">
            {[["What you get", "#features"], ["AI & automation", "#ai"], ["Watch", "#watch"], ["Pricing", "#access"], ["Request access", "#enquire"]].map(([l, h]) => (
              <a key={h} href={h} className="text-sm text-muted-foreground transition-colors hover:text-white">{l}</a>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {memberToken ? (
              <Link to="/dashboard" data-testid="nav-member-login-button"
                className="inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl border border-white/15 px-4 text-xs font-semibold uppercase tracking-wider text-slate-200 transition-colors hover:bg-white/[0.08] hover:text-white">
                Go to my CRM <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ) : (
              <Link to="/login" data-testid="nav-member-login-button"
                className="inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl border border-white/15 px-4 text-xs font-semibold uppercase tracking-wider text-slate-200 transition-colors hover:bg-white/[0.08] hover:text-white">
                <Lock className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Already a</span> member
              </Link>
            )}
            <a href={MB_SITE} target="_blank" rel="noreferrer" data-testid="nav-learn-more-button"
              className="inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl bg-brand-red px-4 text-xs font-semibold uppercase tracking-wider text-white shadow-lg shadow-brand-red/25 transition-colors hover:bg-brand-redDark">
              Learn more <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </nav>
      </header>

      {/* ---------------- hero ---------------- */}
      <section className="mx-auto max-w-7xl px-4 pb-8 pt-16 sm:px-6 sm:pt-24">
        <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-6 xl:col-span-5">
            <Reveal><Eyebrow tone="amber">€499 / month · or free for life with the consultancy</Eyebrow></Reveal>
            <Reveal delay={80}>
              <h1 className="mt-6 font-display text-4xl font-bold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl">
                The private operating system for <span className="mb-text-gradient">Moroccan short-let</span> property.
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
                Source it, negotiate it, close it, run it. Every deal, agent, contact and number for your Moroccan
                Airbnb portfolio in one place — with Claude reading your deals and WhatsApp one tap from every agent.
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <a href={MB_SITE} target="_blank" rel="noreferrer" data-testid="hero-consultancy-apply-btn"
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-brand-red px-7 py-4 font-semibold text-white shadow-xl shadow-brand-red/30 transition-colors hover:bg-brand-redDark">
                  Get the software — see the two ways in
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </a>
                <Link to="/login" data-testid="hero-already-member-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/[0.03] px-6 py-4 font-semibold text-white transition-colors hover:bg-white/[0.08]">
                  <Lock className="h-4 w-4" /> Already a member
                </Link>
              </div>
              <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 text-brand-green" />
                License it at €499/month — or join the Moroccan Brothers consultancy and keep it for life.
              </p>
            </Reveal>
          </div>

          <Reveal delay={300} className="lg:col-span-6 lg:col-start-7 xl:col-span-7">
            <Shot src="/marketing/pipeline.jpeg" alt="The property deal pipeline inside the Moroccan Brothers CRM"
              session="Deal Pipeline · Marrakech · live session" className="lg:-mr-24 xl:-mr-32" />
          </Reveal>
        </div>

        {/* honest capability tape */}
        <Reveal delay={120}>
          <div className="mt-20 grid divide-white/[0.06] rounded-2xl border border-white/[0.07] bg-card/30 sm:grid-cols-2 sm:divide-x lg:grid-cols-4">
            {[
              ["Built for Morocco", "Dirham-first, with £ and € worked out on every deal"],
              ["One-click imports", "Mubawab listings and agent numbers, no retyping"],
              ["Vetted Black Book", "Notaries, lawyers, banks and registries we already use"],
              ["Private workspace", "Your deals stay yours — isolated per member"],
            ].map(([t, d]) => (
              <div key={t} className="px-6 py-5">
                <p className="font-display text-sm font-semibold text-white">{t}</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{d}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ---------------- why it matters ---------------- */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Why it exists</Eyebrow>
            <h2 className="mt-5 font-display text-2xl font-semibold leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
              Buying property 1,500 miles away is where people lose money.
            </h2>
          </Reveal>
          <div className="space-y-5 lg:col-span-6 lg:col-start-7">
            {[
              ["Deals go cold in your inbox", "Three agents, nine listings, two viewings booked and a price you agreed verbally — all in different chats. The board keeps every deal and its real number in front of you."],
              ["The wrong notary costs you months", "You don't need a hundred contacts, you need the right five. We've already found them, and written down what each one is for."],
              ["Nobody can help you if they can't see you", "Your success manager sees exactly which stage you're at and how long you've been there, so you get chased — in the good way."],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={i * 90}>
                <div className="rounded-2xl border border-white/[0.07] bg-card/40 p-6">
                  <h3 className="font-display text-lg font-medium text-white">{t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- feature showcases ---------------- */}
      <section id="features" className="mx-auto max-w-7xl scroll-mt-24 px-4 sm:px-6">
        <Reveal>
          <div className="max-w-2xl">
            <Eyebrow>What's inside</Eyebrow>
            <h2 className="mt-5 font-display text-2xl font-semibold leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
              The tools our members actually use, every week.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              These are real screens from the software — not mock-ups.
            </p>
          </div>
        </Reveal>

        <div className="mt-16 space-y-28">
          {FEATURES.map((f, i) => {
            const flip = i % 2 === 1;
            return (
              <div key={f.key} id={f.key} className="scroll-mt-24">
                <div className={`grid items-center gap-10 lg:grid-cols-12 lg:gap-14 ${flip ? "" : ""}`}>
                  <Reveal className={`lg:col-span-5 ${flip ? "lg:order-2 lg:col-start-8" : ""}`}>
                    <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl border ${f.tone === "green" ? "border-brand-green/25 bg-brand-green/10 text-brand-green" : "border-brand-red/25 bg-brand-red/10 text-brand-red"}`}>
                      <f.icon className="h-5 w-5" />
                    </span>
                    <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{f.eyebrow}</p>
                    <h3 className="mt-2 font-display text-2xl font-semibold leading-tight tracking-tight text-white sm:text-3xl">{f.title}</h3>
                    <p className="mt-4 text-base leading-relaxed text-muted-foreground">{f.body}</p>
                    <ul className="mt-6 space-y-2.5">
                      {f.points.map((p) => (
                        <li key={p} className="flex items-start gap-2.5 text-sm text-slate-300">
                          <Check className={`mt-0.5 h-4 w-4 shrink-0 ${f.tone === "green" ? "text-brand-green" : "text-brand-red"}`} /> {p}
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                  <Reveal delay={120} className={`lg:col-span-7 ${flip ? "lg:order-1 lg:col-start-1" : ""}`}>
                    <Shot src={f.shot} alt={f.title} session={f.session} tone={f.tone} />
                  </Reveal>
                </div>
              </div>
            );
          })}
        </div>

        {/* extras bento */}
        <div className="mt-28 grid gap-4 sm:grid-cols-2">
          {EXTRAS.map((e, i) => (
            <Reveal key={e.title} delay={i * 80}>
              <div className="h-full rounded-2xl border border-white/[0.07] bg-card/40 p-6 transition-colors hover:border-white/15">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-white/[0.05] text-white"><e.icon className="h-4 w-4" /></span>
                <h3 className="mt-4 font-display text-lg font-medium text-white">{e.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{e.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- AI + automation ---------------- */}
      <section id="ai" className="relative scroll-mt-24 overflow-hidden py-28">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/2 top-0 h-px w-[80%] -translate-x-1/2 bg-gradient-to-r from-transparent via-brand-red/40 to-transparent" />
          <div className="absolute left-1/4 top-24 h-[380px] w-[380px] rounded-full bg-brand-red/[0.09] blur-[110px]" />
          <div className="absolute right-[12%] bottom-10 h-[320px] w-[320px] rounded-full bg-brand-green/[0.08] blur-[110px]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <div className="max-w-3xl">
              <Eyebrow>Claude AI · WhatsApp · Automation</Eyebrow>
              <h2 className="mt-5 font-display text-3xl font-semibold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-5xl">
                Morocco's acquisition process has been done by hand for fifty years.{" "}
                <span className="mb-text-gradient">Not any more.</span>
              </h2>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">
                Listings in French. Prices in dirhams. Agents on WhatsApp. Paperwork in a notary's drawer. We wired
                the whole thing into one system — with Claude sitting on top of your pipeline, reading every deal
                like an analyst who already knows the market.
              </p>
            </div>
          </Reveal>

          <div className="mt-16 grid items-start gap-12 lg:grid-cols-12 lg:gap-10">
            <Reveal className="lg:col-span-7">
              <Shot src="/marketing/ai.jpeg" alt="Claude deal analysis with yield, rating and a negotiation script"
                session="AI Deal Analysis · Riad Zitoun el Jdid · Claude Sonnet" />
              <p className="mt-3 pl-1 text-xs text-muted-foreground">
                A real analysis from inside the app — yield, payback, a 7.5/10 rating and the exact words to send the agent.
              </p>
            </Reveal>
            <div className="space-y-4 lg:col-span-5">
              {AI_POINTS.map((a, i) => (
                <Reveal key={a.t} delay={i * 90}>
                  <div className="group rounded-2xl border border-white/[0.07] bg-card/50 p-5 transition-colors hover:border-brand-red/25">
                    <div className="flex items-start gap-3.5">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-brand-red/20 bg-brand-red/10 text-brand-red transition-transform group-hover:scale-105">
                        <a.icon className="h-4 w-4" />
                      </span>
                      <div>
                        <h3 className="font-display text-base font-medium text-white">{a.t}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{a.d}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={120}>
            <div className="mt-14 grid items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <h3 className="font-display text-2xl font-semibold leading-tight tracking-tight text-white">
                  Every agent, from cold number to closing partner.
                </h3>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  Agents get their own board: Contact → Answered → Accepts sous-location → Active → Disqualified.
                  Call or WhatsApp straight from the card, log what they said, and watch which ones actually bring
                  you deals worth closing.
                </p>
              </div>
              <div className="lg:col-span-7">
                <Shot src="/marketing/agent-pipeline.jpeg" alt="The agent pipeline with call and WhatsApp buttons on every card"
                  session="Agent CRM · sourcing pipeline" tone="green" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- exclusivity ---------------- */}
      <section id="access" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-28 sm:px-6">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-amber-500/20 bg-[#0b0f13] p-8 sm:p-12">
            <div aria-hidden className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-amber-500/10 blur-3xl" />
            <div className="relative grid gap-12 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <Eyebrow tone="amber">Two ways in</Eyebrow>
                <h2 className="mt-5 font-display text-2xl font-semibold leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
                  €499 a month for the software. Or join the consultancy and keep it for life.
                </h2>
                <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                  You can license the platform on its own and run your own race. But it was built beside the
                  mentorship — the importer, the Black Book and the deal board exist because of how we buy in
                  Morocco. Join the consultancy and the software comes with you for life, along with the people
                  who already know which notary, which agent and which number to walk away from.
                </p>
                <ol className="mt-8 space-y-4">
                  {STEPS.map((s, i) => (
                    <Reveal key={s.n} delay={i * 100}>
                      <li className="flex gap-5 rounded-2xl border border-white/[0.07] bg-white/[0.02] p-5">
                        <span className="font-display text-2xl font-bold text-white/15">{s.n}</span>
                        <div>
                          <p className="font-display text-base font-medium text-white">{s.t}</p>
                          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
                        </div>
                      </li>
                    </Reveal>
                  ))}
                </ol>
                <p className="mt-5 flex items-center gap-2 px-1 text-xs text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5 text-brand-green" />
                  Every sign-up is approved by hand by a Moroccan Brothers success manager.
                </p>
              </div>

              <div className="grid gap-5 lg:col-span-6 lg:col-start-7 lg:content-start">
                {PLANS.map((p, i) => (
                  <Reveal key={p.key} delay={i * 110}>
                    <div data-testid={`plan-${p.key}`} className={`relative overflow-hidden rounded-2xl border p-7 ${
                      p.highlight ? "border-brand-red/40 bg-gradient-to-br from-brand-red/[0.10] via-white/[0.02] to-brand-green/[0.06]" : "border-white/[0.09] bg-white/[0.02]"}`}>
                      {p.highlight && (
                        <span className="absolute right-5 top-5 rounded-full border border-brand-green/30 bg-brand-green/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-green">
                          Best value
                        </span>
                      )}
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{p.name}</p>
                      <p className="mt-3 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{p.price}</p>
                      <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">{p.per}</p>
                      <p className="mt-4 text-sm text-slate-300">{p.tagline}</p>
                      <ul className="mt-5 space-y-2.5 border-t border-white/[0.07] pt-5">
                        {p.points.map((pt) => (
                          <li key={pt} className="flex items-start gap-2.5 text-sm text-slate-300">
                            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.highlight ? "text-brand-green" : "text-muted-foreground"}`} /> {pt}
                          </li>
                        ))}
                      </ul>
                      <a href={MB_SITE} target="_blank" rel="noreferrer" data-testid={`plan-cta-${p.key}`}
                        className={`mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3.5 font-semibold transition-colors ${
                          p.highlight ? "bg-brand-red text-white shadow-lg shadow-brand-red/25 hover:bg-brand-redDark"
                            : "border border-white/15 bg-white/[0.03] text-white hover:bg-white/[0.08]"}`}>
                        {p.cta} <ArrowUpRight className="h-4 w-4" />
                      </a>
                    </div>
                  </Reveal>
                ))}
                <Reveal delay={240}>
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.02] px-6 py-5">
                    <p className="text-sm text-muted-foreground">Already enrolled or licensed?</p>
                    <Link to="/login" data-testid="exclusivity-login-btn"
                      className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.03] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/[0.08]">
                      <Lock className="h-4 w-4" /> Member sign in
                    </Link>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ---------------- videos & podcasts ---------------- */}
      <section id="watch" className="mx-auto max-w-7xl scroll-mt-24 border-t border-white/[0.06] px-4 py-24 sm:px-6">
        <Reveal>
          <Eyebrow>Watch it work</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-display text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl">
            Real people, real units, real numbers.
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
            Before you take our word for it, watch what members have actually built in Morocco — the purchase
            prices, the monthly income and how they run it from abroad.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {CASE_VIDEOS.map((v, i) => (
            <Reveal key={v.id} delay={i * 90}>
              <VideoCard id={v.id} title={v.title} meta={v.note} testid={`case-video-${v.id}`} />
            </Reveal>
          ))}
        </div>

        <div className="mt-20 flex flex-wrap items-end justify-between gap-4">
          <Reveal>
            <div>
              <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-red">
                <Mic className="h-3.5 w-3.5" /> The podcasts
              </span>
              <h3 className="mt-4 max-w-2xl font-display text-2xl font-semibold leading-tight tracking-tight text-white sm:text-3xl">
                More on the brothers, in their own words.
              </h3>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">
                Long-form interviews on how the business was built, why Morocco, and what buying there really takes.
              </p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <a href="https://www.youtube.com/@moroccanbrothers" target="_blank" rel="noreferrer"
              data-testid="watch-youtube-channel-link"
              className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.03] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/[0.08]">
              <Youtube className="h-4 w-4" /> More on YouTube <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </Reveal>
        </div>

        <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {PODCASTS.map((v, i) => (
            <Reveal key={v.id} delay={i * 80}>
              <VideoCard id={v.id} title={v.title} meta={v.show} testid={`podcast-video-${v.id}`} size="sm" />
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- request access ---------------- */}
      <section id="enquire" className="relative scroll-mt-24 border-t border-white/[0.06] py-24">
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_60%_at_80%_20%,rgba(200,16,46,0.10),transparent_70%)]" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Request access</Eyebrow>
            <h2 className="mt-5 font-display text-2xl font-semibold leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
              Tell us where you're at. A real person replies.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Whether you want the software on its own or the full consultancy, start here. Your note lands
              directly in our team's console — a success manager reads it, not an autoresponder.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-slate-300">
              {["Reply within one working day", "We'll tell you honestly which route fits", "No pressure, no drip campaign"].map((t) => (
                <li key={t} className="flex items-center gap-2.5"><Check className="h-4 w-4 shrink-0 text-brand-green" /> {t}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-6 lg:col-start-7">
            <div className="rounded-3xl border border-white/[0.09] bg-[#0b0f13] p-6 sm:p-8">
              <EnquiryForm />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- final CTA + footer ---------------- */}
      <footer className="border-t border-white/[0.06]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <Reveal>
            <div className="grid gap-10 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <h2 className="max-w-xl font-display text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl">
                  Ready to build something in Morocco that actually pays?
                </h2>
                <p className="mt-4 max-w-lg text-base leading-relaxed text-muted-foreground">
                  License the platform at €499 a month, or join the consultancy and get it for life — with the
                  mentorship, the contacts and the people who use it every day.
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <a href={MB_SITE} target="_blank" rel="noreferrer" data-testid="footer-consultancy-link"
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-red px-7 py-4 font-semibold text-white shadow-xl shadow-brand-red/30 transition-colors hover:bg-brand-redDark">
                    Learn more at moroccanbrothers.com <ArrowUpRight className="h-4 w-4" />
                  </a>
                  <Link to="/login" data-testid="footer-member-login"
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/[0.03] px-6 py-4 font-semibold text-white transition-colors hover:bg-white/[0.08]">
                    <Lock className="h-4 w-4" /> Already a member
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-4 lg:col-start-9">
                <img src={BRAND.logo} alt="Moroccan Brothers" width="500" height="250"
                  className="h-24 w-auto object-contain sm:h-28" />
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  Moroccan Brothers — guiding wealth and growing legacies. UK-based consultancy for Moroccan
                  short-let property.
                </p>
                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                  <a href={MB_SITE} target="_blank" rel="noreferrer" className="text-muted-foreground transition-colors hover:text-white">Consultancy</a>
                  <Link to="/login" className="text-muted-foreground transition-colors hover:text-white">Member portal</Link>
                  <a href="#features" className="text-muted-foreground transition-colors hover:text-white">What's inside</a>
                  <a href="#watch" className="text-muted-foreground transition-colors hover:text-white">Videos</a>
                  <a href="#enquire" className="text-muted-foreground transition-colors hover:text-white">Request access</a>
                </div>
              </div>
            </div>
          </Reveal>
          <p className="mt-16 border-t border-white/[0.06] pt-6 text-xs text-muted-foreground/70">
            © {new Date().getFullYear()} Moroccan Brothers. Software licensed at €499/month, or included for life with consultancy membership.
          </p>
        </div>
      </footer>
    </div>
  );
}
